// src/config/pricing.js
//
// Single source of truth for tier definitions, limits, features, and pricing.
// Every component imports from here — never hardcode tier data elsewhere.
//
// ┌─────────────────────────────────────────────────────────────────┐
// │  TWO-TIER STRUCTURE (post-migration, 2026-05-30):                │
// │    free  — get-started limits (150 items, 50 sales, 50 expenses) │
// │    pro   — display name "Plus", $10/mo or $96/yr                 │
// │           — unlimited inventory/sales/expenses, 500/wk tracking, │
// │             unlimited with BYOK Shippo                           │
// │                                                                  │
// │  NO FREE TRIAL: decided 2026-05-30. Trial-period-days flows are  │
// │  too easy to abuse (multi-card / throwaway-email), and the $10   │
// │  price point is low enough that a generous Free tier is a better │
// │  funnel than a trial. Users upgrade directly to Plus at $10.     │
// │                                                                  │
// │  NO SCRAPER ORDER CAP: scraped orders now flow into the unified  │
// │  Orders & Tracking page. The previous MAX_SCRAPER_ORDERS limit   │
// │  was redundant since the real cost driver is Shippo tracking     │
// │  (which is already gated by tier + BYOK).                        │
// │                                                                  │
// │  Internal tier ID stays 'pro' for backward compatibility with    │
// │  ~50 call sites that check `currentTier === 'pro'`, isPro(),     │
// │  isProOrHigher(), etc. Only the user-facing label is "Plus".     │
// │                                                                  │
// │  Legacy tier IDs ('starter', 'enterprise') are still recognized  │
// │  by the helper functions (getTierById, isTierAtLeast,            │
// │  canTierAccessFeature) and aliased to 'pro' so any subscription  │
// │  rows that survived the migration with the old plan_type don't   │
// │  break the app.                                                  │
// └─────────────────────────────────────────────────────────────────┘

// ============================================
// Tier IDs (internal)
// ============================================
export const TIER_IDS = {
  FREE: 'free',
  PRO: 'pro',
};

// Tier ordering for hierarchy comparisons (index = rank).
// Only the two REAL tiers. Legacy IDs are resolved via LEGACY_TIER_MAP.
export const TIER_ORDER = ['free', 'pro'];

// Legacy plan_type values (from the old 4-tier era) → modern equivalent.
// Used by getTierById / isTierAtLeast / canTierAccessFeature so users whose
// subscription rows still have plan_type='starter' or 'enterprise' get
// resolved to the Plus tier and don't accidentally land in free state.
// Lifetime maps to 'pro' too — lifetime users get everything Plus has.
export const LEGACY_TIER_MAP = {
  starter: 'pro',
  enterprise: 'pro',
  lifetime: 'pro',
};

// ============================================
// Full Tier Definitions
// ============================================
export const TIERS = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Get started with the basics',
    monthlyPrice: 0,
    yearlyPrice: 0,
    limits: {
      MAX_INVENTORY_ITEMS: 150,
      MAX_SALES: 50,
      MAX_EXPENSES: 50,
      MAX_VAULT_ITEMS: null,     // null = feature not available (gated, not count-limited)
      // Combined weekly quota for the unified Orders & Tracking page —
      // counts scraped orders + tracked packages added per ISO week.
      // Added 2026-05-30: opens up tracking for Free users (which was
      // previously fully Plus-gated) but caps total activity. Plus is
      // unlimited (null).
      MAX_WEEKLY_ORDERS_TRACKING: 25,
    },
    features: [
      'Up to 150 inventory items',
      'Up to 50 sales',
      'Up to 50 expenses',
      '25 orders + packages per week',
      'Basic dashboard',
      'Barcode scanning',
      'Email order import',
      'Discord bot integration',
      'No credit card required',
    ],
    cta: 'Start Free',
    highlighted: false,
    priceIds: { month: null, year: null },
  },
  pro: {
    id: 'pro',                         // internal id stays 'pro' for backward compat
    name: 'Plus',                      // user-facing label
    description: 'Everything unlocked',
    monthlyPrice: 10,
    yearlyPrice: 96,                   // saves $24/yr (~20%) vs monthly
    limits: {
      MAX_INVENTORY_ITEMS: null,       // null = unlimited
      MAX_SALES: null,
      MAX_EXPENSES: null,
      MAX_VAULT_ITEMS: null,           // unlimited (and feature is available)
      MAX_WEEKLY_ORDERS_TRACKING: null, // unlimited on Orders & Tracking page
      // NOTE: MAX_WEEKLY_TRACKING (per-API tracking refresh quota)
      // intentionally absent — that limit will be reintroduced when the
      // new tracking API system ships.
    },
    features: [
      'Unlimited inventory, sales & expenses',
      'Email order import + package tracking',
      'Unlimited package tracking',
      'Unlimited Vault',
      'Export to CSV/PDF/JSON',
      'Reports & analytics',
      'Discord bot integration',
      'Priority support',
    ],
    cta: 'Get Plus',
    highlighted: true,
    priceIds: {
      month: process.env.REACT_APP_STRIPE_PRICE_ID_PRO_MONTH || process.env.REACT_APP_STRIPE_PRICE_ID || null,
      year: process.env.REACT_APP_STRIPE_PRICE_ID_PRO_YEAR || null,
    },
  },
};

// ============================================
// Feature Access Matrix
// Maps each feature to the minimum tier required.
// In the 2-tier world, anything that isn't free-tier-available maps to 'pro'.
// ============================================
export const FEATURE_ACCESS = {
  export: 'pro',
  // package_tracking REMOVED 2026-05-30 — tracking is now available to
  // all tiers, but Free is subject to the combined 25/week orders +
  // packages quota (see MAX_WEEKLY_ORDERS_TRACKING in TIERS.free.limits).
  // Plus = unlimited. Quota enforced both on the frontend (banner +
  // disabled add buttons) and in the can_add_tracking_number DB function.
  reports: 'pro',
  // pre_orders removed 2026-06-13 — feature retired.
  vault: 'pro',
  data_export: 'pro',
  api_access: 'pro',                   // was 'enterprise' — now part of Plus
  // NOTE: byok_tracking removed — the BYOK Shippo flow was tied to the old
  // weekly tracking quota. BYOK isn't a value-prop gate anymore; the
  // Shippo API key entry in Account → Integrations stays as a routing
  // option for Plus users but no longer requires its own feature gate.
};

// ============================================
// Pro-Only Features (constant export, used by ProFeaturePage + others)
// ============================================
export const PRO_ONLY_FEATURES = {
  EXPORT: 'export',
  PACKAGE_TRACKING: 'package_tracking',
  // PRE_ORDERS removed 2026-06-13 — feature retired.
  VAULT: 'vault',
  REPORTS: 'reports',
  DATA_EXPORT: 'data_export',
  // BYOK_TRACKING removed — see FEATURE_ACCESS comment above.
};

// ============================================
// Backward-compat limit exports
// ============================================
export const FREE_LIMITS = TIERS.free.limits;
export const PRO_LIMITS = TIERS.pro.limits;

// ============================================
// Comparison table features — Free vs Plus
// Used by PricingPlans component when showComparison={true}.
// ============================================
export const COMPARISON_FEATURES = [
  { name: 'Inventory tracking', free: '150 items', pro: 'Unlimited' },
  { name: 'Sales logging', free: '50 sales', pro: 'Unlimited' },
  { name: 'Expense tracking', free: '50 expenses', pro: 'Unlimited' },
  { name: 'Email order import', free: true, pro: true },
  { name: 'Basic dashboard', free: true, pro: true },
  { name: 'Multi-device sync', free: true, pro: true },
  { name: 'Barcode scanning', free: true, pro: true },
  { name: 'Buyer management', free: true, pro: true },
  { name: 'Discord bot integration', free: true, pro: true },
  { name: 'Package tracking', free: false, pro: 'Unlimited' },
  { name: 'Export CSV/PDF/JSON', free: false, pro: true },
  { name: 'Reports & analytics', free: false, pro: true },
  { name: 'Vault', free: false, pro: 'Unlimited' },
  { name: 'API access', free: false, pro: true },
];

// ============================================
// Helper Functions
// ============================================

/**
 * Resolve a (possibly legacy) tier ID to the canonical modern ID.
 * 'starter', 'enterprise', 'lifetime' → 'pro'. Unknown IDs pass through.
 */
function resolveLegacyTier(tierId) {
  return LEGACY_TIER_MAP[tierId] || tierId;
}

/**
 * Get a tier object by its ID. Legacy IDs (starter, enterprise, lifetime)
 * resolve to the Plus tier. Unknown IDs fall back to free.
 */
export function getTierById(tierId) {
  const resolved = resolveLegacyTier(tierId);
  return TIERS[resolved] || TIERS.free;
}

/**
 * Reverse lookup: find which tier a Stripe price ID belongs to.
 * Returns the tier object, or null if not found.
 */
export function getTierByPriceId(priceId) {
  if (!priceId) return null;
  for (const tier of Object.values(TIERS)) {
    if (tier.priceIds.month === priceId || tier.priceIds.year === priceId) {
      return tier;
    }
  }
  return null;
}

/**
 * Get the Stripe price ID for a given tier and billing interval.
 * Returns null if the price is not configured.
 */
export function getPriceIdForTier(tierId, interval = 'month') {
  const tier = TIERS[resolveLegacyTier(tierId)];
  if (!tier) return null;
  return tier.priceIds[interval] || null;
}

/**
 * Get the limits object for a given tier. Legacy IDs resolved to Plus.
 */
export function getLimitsForTier(tierId) {
  const tier = TIERS[resolveLegacyTier(tierId)];
  return tier ? tier.limits : TIERS.free.limits;
}

/**
 * Check if a tier meets or exceeds a required tier level.
 * Legacy tier IDs are resolved through LEGACY_TIER_MAP first, so a user
 * still on plan_type='starter' or 'enterprise' counts as Pro for these
 * comparisons.
 *
 * Examples:
 *   isTierAtLeast('pro', 'pro')        → true
 *   isTierAtLeast('starter', 'pro')    → true  (starter resolves to pro)
 *   isTierAtLeast('free', 'pro')       → false
 *   isTierAtLeast('lifetime', 'pro')   → true  (lifetime resolves to pro)
 */
export function isTierAtLeast(currentTier, requiredTier) {
  const current = resolveLegacyTier(currentTier);
  const required = resolveLegacyTier(requiredTier);
  const currentIndex = TIER_ORDER.indexOf(current);
  const requiredIndex = TIER_ORDER.indexOf(required);
  return (currentIndex >= 0 ? currentIndex : 0) >= (requiredIndex >= 0 ? requiredIndex : 0);
}

/**
 * Check if a user's tier can access a specific feature.
 * Uses the FEATURE_ACCESS matrix to determine the minimum required tier.
 */
export function canTierAccessFeature(userTier, feature) {
  const requiredTier = FEATURE_ACCESS[feature];
  // If the feature isn't in the matrix, it's available to all tiers
  if (!requiredTier) return true;
  return isTierAtLeast(userTier || 'free', requiredTier);
}

/**
 * Get the minimum required tier name for a feature (for display).
 * Returns null if the feature is available to all tiers.
 */
export function getRequiredTierForFeature(feature) {
  const requiredTierId = FEATURE_ACCESS[feature];
  if (!requiredTierId) return null;
  return getTierById(requiredTierId);
}

/**
 * Get the next tier up from the current one (for upgrade suggestions).
 * Returns null if already on the highest tier. In the 2-tier world this
 * is just "free → pro" and "pro → null".
 */
export function getNextTier(currentTier) {
  const current = resolveLegacyTier(currentTier || 'free');
  const currentIndex = TIER_ORDER.indexOf(current);
  if (currentIndex < 0 || currentIndex >= TIER_ORDER.length - 1) return null;
  return TIERS[TIER_ORDER[currentIndex + 1]];
}
